/* iter55o — Global Genre Variants library editor.
 *
 * Admin can add/edit/remove reusable genre palettes (mafia, stalker,
 * ceo, monster, doctor, vampire, …). Any beat-map template that ships
 * with `genre_variants: {slug: {...}}` picks them up automatically.
 * These are stored on `app_config.global.genre_variants`.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { adminApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Save, Loader2, Plus, Trash2, RefreshCcw, Skull, Download,
} from 'lucide-react';
import { toast } from 'sonner';

const BLANK = () => ({
  slug:   '',
  label:  '',
  values: [{ key: 'external_threat', value: '' }],
});

function VariantCard({ v, onChange, onRemove }) {
  const update = (patch) => onChange({ ...v, ...patch });
  const updateKV = (i, patch) => update({
    values: v.values.map((row, j) => (i === j ? { ...row, ...patch } : row)),
  });
  return (
    <div className="rounded-md border border-border bg-card p-3"
         data-testid={`genre-variant-card-${v.slug || 'new'}`}>
      <div className="grid gap-2 sm:grid-cols-2">
        <div>
          <Label className="text-[10.5px] uppercase tracking-wider">Slug</Label>
          <Input value={v.slug}
                  onChange={(e) => update({ slug: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
                  placeholder="mafia"
                  data-testid={`genre-variant-slug-${v.slug || 'new'}`} />
        </div>
        <div>
          <Label className="text-[10.5px] uppercase tracking-wider">Display label</Label>
          <Input value={v.label} onChange={(e) => update({ label: e.target.value })}
                  placeholder="Mafia"
                  data-testid={`genre-variant-label-${v.slug || 'new'}`} />
        </div>
      </div>
      <div className="mt-2">
        <div className="mb-1 flex items-center justify-between">
          <Label className="text-[10.5px] uppercase tracking-wider">Substitutions</Label>
          <button type="button"
                  onClick={() => update({ values: [...v.values, { key: '', value: '' }] })}
                  className="inline-flex items-center gap-1 text-[10px] text-primary hover:underline"
                  data-testid={`genre-variant-add-kv-${v.slug || 'new'}`}>
            <Plus className="h-2.5 w-2.5" /> Add row
          </button>
        </div>
        {v.values.map((row, i) => (
          <div key={`kv-${v.slug || 'new'}-${i}`} className="mb-1 flex items-center gap-1.5">
            <Input value={row.key}
                    onChange={(e) => updateKV(i, { key: e.target.value.replace(/[^a-z0-9_]/gi, '') })}
                    placeholder="external_threat"
                    className="h-7 flex-1 text-[11.5px] font-mono-stat"
                    data-testid={`genre-variant-kv-key-${v.slug || 'new'}-${i}`} />
            <span className="text-[10.5px] text-muted-foreground">→</span>
            <Input value={row.value}
                    onChange={(e) => updateKV(i, { value: e.target.value })}
                    placeholder="rival family"
                    className="h-7 flex-[2] text-[11.5px]"
                    data-testid={`genre-variant-kv-value-${v.slug || 'new'}-${i}`} />
            <button type="button"
                    onClick={() => update({ values: v.values.filter((_, j) => j !== i) })}
                    className="rounded-sm p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                    data-testid={`genre-variant-kv-remove-${v.slug || 'new'}-${i}`}>
              <Trash2 className="h-3 w-3" />
            </button>
          </div>
        ))}
      </div>
      <div className="mt-2 flex justify-end">
        <Button variant="ghost" size="sm" onClick={onRemove}
                className="h-7 gap-1 text-[10.5px] text-destructive"
                data-testid={`genre-variant-remove-${v.slug || 'new'}`}>
          <Trash2 className="h-3 w-3" /> Remove variant
        </Button>
      </div>
    </div>
  );
}

export default function GenreVariantsAdminPanel() {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminApi.genreVariantsGet();
      const dict = r?.variants || {};
      setRows(Object.entries(dict).map(([slug, v]) => ({
        slug, label: v.label || slug,
        values: Object.entries(v.values || {}).map(([k, val]) => ({ key: k, value: String(val) })),
      })));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load genre variants.');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const addRow = () => setRows((prev) => [...prev, BLANK()]);

  // iter55p6 — One-click CSV export of the current row set. Format:
  //   slug,label,key,value
  // one row per key/value pair so contributors can eyeball the whole
  // palette in any spreadsheet tool.
  const exportCsv = () => {
    const esc = (s) => {
      const str = String(s ?? '');
      if (/[",\n]/.test(str)) return `"${str.replace(/"/g, '""')}"`;
      return str;
    };
    const lines = ['slug,label,key,value'];
    for (const r of rows) {
      const slug = (r.slug || '').trim();
      const label = (r.label || slug).trim();
      if (!slug) continue;
      if (!r.values.length) {
        lines.push([slug, label, '', ''].map(esc).join(','));
        continue;
      }
      for (const kv of r.values) {
        lines.push([slug, label, kv.key || '', kv.value || ''].map(esc).join(','));
      }
    }
    const blob = new Blob([lines.join('\n') + '\n'], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const stamp = new Date().toISOString().slice(0, 10);
    a.download = `genre-variants-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} variant${rows.length === 1 ? '' : 's'} to CSV`);
  };

  const save = async () => {
    const payload = {};
    for (const r of rows) {
      const slug = (r.slug || '').trim().toLowerCase();
      if (!slug) { toast.error('Every variant needs a slug.'); return; }
      const values = {};
      for (const { key, value } of r.values) {
        const k = (key || '').trim();
        if (!k) continue;
        values[k] = value ?? '';
      }
      payload[slug] = { label: (r.label || slug).trim(), values };
    }
    setSaving(true);
    try {
      const r = await adminApi.genreVariantsPut(payload);
      toast.success(`Saved ${r.count} genre variant${r.count === 1 ? '' : 's'}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-3" data-testid="admin-genre-variants-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="inline-flex items-center gap-2 text-[15px] font-semibold">
            <Skull className="h-4 w-4 text-primary" /> Genre variants
          </h2>
          <p className="text-[11.5px] text-muted-foreground">
            Reusable substitution palettes for beat-map templates. Any template
            referencing <code>{'{{external_threat}}'}</code>, <code>{'{{territory}}'}</code>,
            <code>{'{{grand_gesture}}'}</code>, or any custom key you define here
            will offer these as a radio at apply-time.
          </p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="ghost" onClick={load} className="gap-1.5"
                  data-testid="admin-genre-variants-reload">
            <RefreshCcw className="h-3.5 w-3.5" /> Reload
          </Button>
          <Button size="sm" variant="ghost" onClick={exportCsv} disabled={rows.length === 0}
                  className="gap-1.5"
                  data-testid="admin-genre-variants-export-csv">
            <Download className="h-3.5 w-3.5" /> Export CSV
          </Button>
          <Button size="sm" variant="outline" onClick={addRow} className="gap-1.5"
                  data-testid="admin-genre-variants-add">
            <Plus className="h-3.5 w-3.5" /> Add variant
          </Button>
          <Button size="sm" onClick={save} disabled={saving} className="gap-1.5"
                  data-testid="admin-genre-variants-save">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            Save all
          </Button>
        </div>
      </div>

      {/* iter55p5 — Self-serve docs card. Explains the token-swap
          engine so ops can add new genres (dark academia, alien,
          dragon lord, etc.) without asking engineering. */}
      <details className="rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-[11.5px] leading-relaxed"
                data-testid="admin-genre-variants-docs">
        <summary className="cursor-pointer text-[12px] font-semibold text-amber-800 dark:text-amber-300">
          How to add a new genre (2-minute read)
        </summary>
        <div className="mt-2 space-y-2 text-foreground">
          <p>
            Every beat-map template contains <code>{'{{token}}'}</code> placeholders
            (e.g. <code>{'{{external_threat}}'}</code>, <code>{'{{territory}}'}</code>,
            <code>{'{{power_base}}'}</code>, <code>{'{{grand_gesture}}'}</code>).
            A <strong>Genre variant</strong> is a palette of concrete phrases those
            tokens resolve to for a specific flavour (Mafia → "rival Bratva family",
            Dark Academia → "the fellowship board of trustees").
          </p>
          <p>
            <strong>To add a new genre:</strong>
          </p>
          <ol className="ml-4 list-decimal space-y-1">
            <li>
              Click <em>Add variant</em>. Pick a lowercase slug (
              <code>dragon_lord</code>, <code>dark_academia</code>,
              <code>alien</code>) — it's the internal handle.
            </li>
            <li>
              Set a display label (<em>Dragon Lord</em>) — this is what authors see
              on the chip.
            </li>
            <li>
              Fill in one <code>key → value</code> substitution per row. Match the
              keys that already ship in the beat map (open the Starter Templates
              admin panel to see them); missing keys just leave the placeholder
              text visible in the generated chapters, which is safe but ugly.
            </li>
            <li>
              Click <em>Save all</em>. Every beat-map template that ships with
              <code> genre_variants </code> immediately offers your new slug on
              its chip row — writers see it live on next page load.
            </li>
          </ol>
          <p>
            <strong>Blending:</strong> Authors can now flip a
            <code> Blend two </code> toggle inside the template picker and select
            2+ genres at once. Later selections override earlier ones on token
            collision — so Mafia + Monster resolves <code>{'{{external_threat}}'}</code>
            to Monster's "hunters' guild" but keeps Mafia's <code>{'{{territory}}'}</code>
            unless Monster also overrides it.
          </p>
          <p>
            <strong>Tips:</strong> Keep values concrete and sensory (
            <em>"the volcanic aerie"</em> beats <em>"his lair"</em>). Aim for 8-10
            substitution keys minimum per genre so no beat leaks an unresolved
            <code> {'{{...}}'} </code> marker. Test a variant before publishing
            by picking it in the Starter Templates preview and skimming the
            Before / After Diff panel.
          </p>
        </div>
      </details>

      {loading ? (
        <div className="flex items-center gap-2 text-[11.5px] text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" /> Loading…
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-md border border-dashed border-border p-6 text-center text-[12px] text-muted-foreground">
          No variants yet. Click <strong>Add variant</strong> to create one — e.g.
          slug <code>monster</code> · label <em>Monster</em> · substitutions
          {' '}<code>external_threat → the hunters guild</code>,
          {' '}<code>territory → the crypt</code>,
          {' '}<code>grand_gesture → severs his own bond to the shadow court</code>.
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {rows.map((v, i) => (
            <VariantCard key={`${v.slug || 'new'}-${i}`} v={v}
              onChange={(next) => setRows((prev) => prev.map((r, j) => (j === i ? next : r)))}
              onRemove={() => setRows((prev) => prev.filter((_, j) => j !== i))}
            />
          ))}
        </div>
      )}
    </div>
  );
}
